import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, { Bilan as BilanData, Rubrique } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

const RubriqueBlock: React.FC<{ rubrique: Rubrique }> = ({ rubrique }) => (
  <div className="mb-4">
    <div className="flex justify-between font-semibold text-gray-800 border-b border-gray-200 pb-1">
      <span>{rubrique.libelle}</span>
      <span>{formatTND(rubrique.total)}</span>
    </div>
    {rubrique.lignes.length === 0 ? (
      <div className="text-xs text-gray-400 py-1">—</div>
    ) : rubrique.lignes.map((l, i) => (
      <div key={`${l.numero}-${i}`} className="flex justify-between text-sm text-gray-600 py-0.5">
        <span><span className="font-mono text-gray-400 mr-2">{l.numero}</span>{l.libelle}</span>
        <span className={l.montant < 0 ? 'text-red-600' : ''}>{formatTND(l.montant)}</span>
      </div>
    ))}
  </div>
);

const Bilan: React.FC = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState<BilanData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await comptabiliteService.bilan(startDate, endDate));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Bilan</h1>
        <p className="text-sm text-gray-500">
          Situation patrimoniale à la date de fin (modèle SCE tunisien). Le résultat de la période
          est intégré aux capitaux propres.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Du</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Au</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-10">Chargement…</div>
      ) : !data ? null : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Actif</h2>
              <RubriqueBlock rubrique={data.actifNonCourant} />
              <RubriqueBlock rubrique={data.actifCourant} />
              <div className="flex justify-between py-2 px-3 rounded-lg bg-gray-900 text-white font-bold">
                <span>Total actif</span>
                <span>{formatTND(data.totalActif)}</span>
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl border border-gray-200">
              <h2 className="text-lg font-bold text-gray-900 mb-4">Capitaux propres et passifs</h2>
              <RubriqueBlock rubrique={data.capitauxPropres} />
              <RubriqueBlock rubrique={data.passifNonCourant} />
              <RubriqueBlock rubrique={data.passifCourant} />
              <div className="flex justify-between py-2 px-3 rounded-lg bg-gray-900 text-white font-bold">
                <span>Total capitaux propres et passifs</span>
                <span>{formatTND(data.totalCapitauxPropresEtPassifs)}</span>
              </div>
            </div>
          </div>

          {!data.equilibre && (
            <p className="text-sm text-red-600">
              ⚠ Bilan déséquilibré (total actif ≠ total passif). Vérifiez que toutes les opérations
              de la période ont été comptabilisées.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default Bilan;
